import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { fetchCalendarByRefForDate } from "../Appointments/TabContent/api/AppointmentsApi";
import { Building2, Pencil } from "lucide-react";
import DockDoorSettings from "../Appointments/TabContent/Calendar-Main/DockDoorSettings";
import EditWarehouseModal from "../Manage-Team/TabContent/ViewTab/EditWarehouseModal";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { linkifyText, linkifyEmails } from "../../../components/ACS-Dashboard/ACS-Utils/textFormatting";

const ViewLocation = ({ location }) => {
  const [currentLocation, setCurrentLocation] = useState(location || null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isShipper, setIsShipper] = useState(false);
  const [formValues, setFormValues] = useState({
    appointmentUrl: "",
    contactName: "",
    phoneNumber: "",
    email: "",
    latitude: "",
    longitude: "",
    notes: "",
  });
  const [calendarSettings, setCalendarSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [openEditWarehouseModal, setOpenEditWarehouseModal] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);

  // Check if user is a shipper from localStorage
  useEffect(() => {
    try {
      const storedData = localStorage.getItem('organizationTypes');
      if (storedData) {
        const organizationTypes = JSON.parse(storedData);

      const isShipper =
        (Array.isArray(organizationTypes) &&
          organizationTypes.some(type =>
            typeof type === "string" && type.toLowerCase().includes("shipper")
          )) ||
        (typeof organizationTypes === "string" &&
          organizationTypes.toLowerCase().includes("shipper"));
        setIsShipper(isShipper);
      }
    } catch (err) {
      console.error("Failed to parse organizationTypes from localStorage:", err);
      setIsShipper(false);
    }
  }, []);


  const formatDurationToHours = (durationInMinutes = 120) => {
    return (durationInMinutes / 60)
      .toFixed(2)
      .replace(/\.00$/, "");
  };

  // Update currentLocation when location prop changes
  useEffect(() => {
    setCurrentLocation(location || null);
  }, [location]);

  useEffect(() => {
    const fetchFreshLocationData = async () => {
      if (location?.id) {
        try {
          const { data } = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/locations/${location.id}`
          );
          setCurrentLocation(data);
        } catch (err) {
          console.error("Failed to fetch fresh location data:", err);
        }
      }
    };

    fetchFreshLocationData();
  }, [location?.id]); // Refetch when location ID changes

  // Update formValues whenever currentLocation changes
  useEffect(() => {
    if (currentLocation) {
      setFormValues({
        appointmentUrl: currentLocation.appointmentUrl ?? "",
        contactName: currentLocation.contactName ?? currentLocation.contactPerson ?? "",
        phoneNumber: currentLocation.phoneNumber ?? "",
        email: currentLocation.email ?? "",
        latitude: currentLocation.latitude ?? "",
        longitude: currentLocation.longitude ?? "",
        notes: currentLocation.notes ?? "",
      });
    }
  }, [currentLocation]);

  const transformCalendarResponseToSettings = useCallback((apiResponse) => {
    const regularHours = {};
    const holidays = [];
    const blackouts = [];

    for (const evt of apiResponse?.event || []) {
      const recurringType = evt.event_recurring_type || evt.recurringType;
      const statusType = evt.event_statue_type || evt.eventStatusType || evt.eventTypeName;

      if (
        recurringType &&
        statusType &&
        recurringType.toUpperCase() === "WEEKLY" &&
        statusType.toUpperCase() === "OPEN"
      ) {
        const day =
          evt.dayOfWeek &&
          evt.dayOfWeek.charAt(0).toUpperCase() + evt.dayOfWeek.slice(1).toLowerCase();
        if (day) {
          regularHours[day] = {
            from: evt.startTime?.slice(0, 5) || "",
            to: evt.endTime?.slice(0, 5) || "",
            eventId: evt.eventId,
          };
        }
      }

      if (
        recurringType &&
        statusType &&
        recurringType.toUpperCase() === "YEARLY" &&
        statusType.toUpperCase() === "CLOSE"
      ) {
        const month = evt.monthOfYear?.toString().padStart(2, "0");
        const day = evt.dayOfMonth?.toString().padStart(2, "0");
        if (month && day) {
          holidays.push({
            date: `2025-${month}-${day}`,
            allDay: true,
            reason: evt.eventDescription,
            eventId: evt.eventId,
          });
        }
      }
    }

    for (const ex of apiResponse?.exceptionEvent || []) {
      if ((ex.overrideType || "").toUpperCase() === "CLOSE") {
        blackouts.push({
          from: ex.startDate,
          to: ex.endDate || ex.startDate,
          allDay: ex.isWholeDay === true,
          reason: ex.reason,
          exceptionId: ex.exceptionId,
        });
      }
    }

    return {
      regularHours,
      holidays,
      blackouts,
      appointmentDuration: apiResponse?.appointmentDuration,
      calendarId: apiResponse?.calendarId,
      calendarRefId: apiResponse?.calendarRefId,
    };
  }, []);

  const fetchCalendarSettings = useCallback(async () => {
    const calendarRefId = currentLocation?.calendar?.calendarRefId;
    if (!calendarRefId) {
      setCalendarSettings(null);
      return;
    }

    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const data = await fetchCalendarByRefForDate(calendarRefId);
      setCalendarSettings(transformCalendarResponseToSettings(data || {}));
    } catch (err) {
      console.error("Failed to load calendar settings:", err);
      setSettingsError("Unable to load calendar settings.");
      setCalendarSettings(null);
    } finally {
      setSettingsLoading(false);
    }
  }, [currentLocation?.calendar?.calendarRefId, transformCalendarResponseToSettings]);

  useEffect(() => {
    fetchCalendarSettings();
  }, [fetchCalendarSettings]);

  const handleWarehouseSaved = useCallback(async () => {
    await fetchCalendarSettings();
    setOpenEditWarehouseModal(false);
  }, [fetchCalendarSettings]);

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return value;
  };

  const handleInputChange = (key, value) =>
    setFormValues((prev) => ({ ...prev, [key]: value }));

  const handlePhoneBlur = () => {
    setFormValues((prev) => ({
      ...prev,
      phoneNumber: formatPhoneNumber(prev.phoneNumber),
    }));
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setFormValues({
      appointmentUrl: currentLocation?.appointmentUrl ?? "",
      contactName:
        currentLocation?.contactName ?? currentLocation?.contactPerson ?? "",
      phoneNumber: currentLocation?.phoneNumber ?? "",
      email: currentLocation?.email ?? "",
      latitude: currentLocation?.latitude ?? "",
      longitude: currentLocation?.longitude ?? "",
      notes: currentLocation?.notes ?? "",
    });
  };

  const handleSave = async () => {
    if (!currentLocation?.id) return;
    setIsSaving(true);
    setError(null);

    const notesValue =
      formValues.notes === undefined || formValues.notes.trim() === ""
        ? null
        : formValues.notes;

    const payload = {
      id: currentLocation.id,
      locationName: currentLocation.locationName,
      addressLine1: currentLocation.addressLine1,
      addressLine2: currentLocation.addressLine2,
      city: currentLocation.city,
      state: currentLocation.state,
      zipCode: currentLocation.zipCode,
      country: currentLocation.country,
      latitude: formValues.latitude === "" ? null : Number(formValues.latitude),
      longitude: formValues.longitude === "" ? null : Number(formValues.longitude),
      appointmentUrl: formValues.appointmentUrl || null,
      contactName: formValues.contactName || null,
      phoneNumber: formValues.phoneNumber || null,
      email: formValues.email || null,
      notes: notesValue,
      fax: currentLocation.fax || null,
      customLocationIdentifier: currentLocation.customLocationIdentifier || null,
      locationRef: currentLocation.locationRef || null,
      locationType: currentLocation.locationType || null,
      locationCode: currentLocation.locationCode || null,
      officeCode: currentLocation.officeCode || null,
      hierarchyPath: currentLocation.hierarchyPath || null,
      isHeadquarters: currentLocation.isHeadquarters || null,

      parentLocation: currentLocation.parentLocation?.id
        ? { id: currentLocation.parentLocation.id }
        : null
    };

    try {
      const { data } = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/locations/Locrefupdate/${currentLocation.locationRef}`,
        payload,
        { headers: { accept: "*/*", "Content-Type": "application/json" } }
      );


      const updated = data && typeof data === "object" ? data : payload;
      setCurrentLocation(updated);
      setIsEditMode(false);

    } catch (err) {
      setError("Failed to update location. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Prepare key-value pairs
  const mapLink =
    currentLocation?.latitude != null &&
    currentLocation?.longitude != null
      ? `https://www.google.com/maps/?q=${currentLocation.latitude},${currentLocation.longitude}`
      : null;

  const entries = {
    "Location Name": currentLocation?.locationName,
    "Address Line 1": currentLocation?.addressLine1,
    "Address Line 2": currentLocation?.addressLine2,
    City: currentLocation?.city,
    State: currentLocation?.state,
    Zip: currentLocation?.zipCode,
    Country: currentLocation?.country,
    "Appointment URL": !isEditMode && formValues.appointmentUrl
      ? linkifyText(formValues.appointmentUrl)
      : formValues.appointmentUrl,
    "Contact Person": formValues.contactName,
    "Phone Number": formValues.phoneNumber,
    Email: !isEditMode && formValues.email
      ? linkifyEmails(formValues.email, "text-blue-600 dark:text-blue-400 hover:underline")
      : formValues.email,
  };

  if (!isEditMode) {
    entries["Map URL"] = mapLink ? (
      <a
        href={mapLink}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        View Map
      </a>
    ) : (
      "—"
    );
  }

  useEffect(() => {
    setMapLoading(!!mapLink);
  }, [mapLink]);

  const editableFieldConfig = {
    "Appointment URL": "appointmentUrl",
    "Contact Person": "contactName",
    "Phone Number": "phoneNumber",
    Email: "email",
    Latitude: "latitude",
    Longitude: "longitude",
  };

  if (!location) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-lg">
        No location data available.
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col transition-opacity duration-500 bg-gray-50 dark:bg-acs-dark-bg">

      {/* === Top Header === */}
      <div className="sticky top-0 z-10 bg-white dark:bg-acs-dark-surface shadow-md p-fluid-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-fluid-3">
        <div className="flex items-center gap-fluid-3">
          <div className="w-20 h-20 rounded-full bg-acs-accent/20 dark:bg-acs-accent/30 flex items-center justify-center shadow-inner">
            <Building2 className="w-10 h-10 text-blue-600 dark:text-acs-accent" />
          </div>

          <div>
            <h2 className="text-fluid-xl font-semibold text-gray-900 dark:text-acs-dark-text">
              {location.locationName || "Untitled Location"}
            </h2>
            {(currentLocation?.city || currentLocation?.state) && (
              <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
                {[currentLocation?.city, currentLocation?.state].filter(Boolean).join(", ")}
              </p>
            )}

            <div className="mt-fluid-1 flex items-center gap-2">
              <span className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-[2px] text-xs rounded-md font-medium">
                Active Location
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* === Main Content === */}
      <div className="flex-1 overflow-y-auto bg-white dark:bg-acs-dark-bg w-full p-fluid-3 grid grid-cols-1 md:grid-cols-2 gap-fluid-3">

        {/* === Left Column - Location Info Card === */}
        <div className="bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3">
          <div className="flex items-center justify-between mb-fluid-2">
            <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
              Location Information
            </h3>
            <div className="flex items-center gap-2">
              {error && (
                <span className="text-xs text-red-500 whitespace-nowrap">
                  {error}
                </span>
              )}
              {isShipper && (
                <>
                  {isEditMode ? (
                    <>
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-3 py-1 text-xs rounded-md bg-blue-600 text-white disabled:opacity-60"
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        className="px-3 py-1 text-xs rounded-md border border-gray-300 text-gray-600"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditMode(true)}
                      className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
                      title="Edit Location"
                    >
                      <Pencil size={16} />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="space-y-3 text-sm text-gray-700 dark:text-acs-dark-text">
            {Object.entries(entries).map(([label, value]) => {
              if (isEditMode && label === "Map URL") return null;
              const fieldKey = editableFieldConfig[label];
              const isEditable = isEditMode && fieldKey;
              return (
                <div key={label} className="flex">
                  <span className="font-medium w-40 text-gray-600 dark:text-acs-dark-subtext">
                    {label}:
                  </span>
                  <span className="text-gray-800 dark:text-acs-dark-text">
                    {isEditable ? (
                      <input
                        type={
                          label === "Latitude" || label === "Longitude"
                            ? "number"
                            : label === "Phone Number"
                            ? "tel"
                            : "text"
                        }
                        step="any"
                        value={formValues[fieldKey] ?? ""}
                        onChange={(e) =>
                          handleInputChange(fieldKey, e.target.value)
                        }
                        onBlur={label === "Phone Number" ? handlePhoneBlur : undefined}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm bg-white"
                      />
                    ) : (
                      value || "—"
                    )}
                  </span>
                </div>
              );
            })}

            {/* Notes Section - Full Width */}
            <div className="flex flex-col pt-2 border-t border-gray-200 dark:border-acs-dark-border">
              <span className="font-medium text-gray-600 dark:text-acs-dark-subtext mb-1">
                Notes:
              </span>
              {isEditMode ? (
                <textarea
                  value={formValues.notes ?? ""}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm bg-white min-h-[100px] resize-y"
                  placeholder="Add any notes about this location..."
                />
              ) : (
                <div className="text-gray-800 dark:text-acs-dark-text whitespace-pre-wrap">
                  {formValues.notes && formValues.notes !== "—"
                    ? linkifyText(formValues.notes)
                    : "—"
                  }
                </div>
              )}
            </div>
          </div>
        </div>

        {/* === Right Column - Map View === */}
        <div className="bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm overflow-hidden flex flex-col">
          {mapLink ? (
            <div className="flex-1 relative">
              {mapLoading && (
                <div className="absolute inset-0 bg-white/60 dark:bg-black/40 flex items-center justify-center z-10">
                  <Loader message="Loading map..." />
                </div>
              )}
              <iframe
                title="Location Map"
                src={`https://maps.google.com/maps?q=${currentLocation.latitude},${currentLocation.longitude}&z=14&output=embed`}
                className="w-full h-full min-h-[300px]"
                loading="lazy"
                allowFullScreen
                onLoad={() => setMapLoading(false)}
              />
            </div>
          ) : (
            <div className="flex-1 min-h-[300px] flex items-center justify-center text-sm text-gray-500 dark:text-acs-dark-subtext bg-gray-100 dark:bg-[#2A2D35]">
              Add latitude and longitude to preview this location on the map.
            </div>
          )}
        </div>

        {currentLocation?.calendar?.calendarRefId && (
          <div className="md:col-span-2 bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3">
            <div className="flex items-center justify-between mb-fluid-2">
              <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                Calendar Configuration
              </h3>
              <div className="flex items-center gap-2">
                {settingsError && (
                  <span className="text-xs text-red-500 whitespace-nowrap">
                    {settingsError}
                  </span>
                )}
                {isShipper && (
                  <button
                    type="button"
                    onClick={() => setOpenEditWarehouseModal(true)}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
                    title="Edit warehouse calendar"
                  >
                    <Pencil size={16} />
                  </button>
                )}
              </div>
            </div>

            {settingsLoading ? (
              <p className="text-gray-500 dark:text-acs-dark-subtext">
                Loading calendar settings...
              </p>
            ) : calendarSettings ? (
              <DockDoorSettings
                settings={calendarSettings}
                onChange={() => {}}
                isViewOnly={true}
                isWarehouseView={true}
              />
            ) : (
              <p className="text-gray-500 dark:text-acs-dark-subtext">
                No calendar settings available.
              </p>
            )}
          </div>
        )}
      </div>

      {openEditWarehouseModal && (
        <EditWarehouseModal
          warehouseName={currentLocation?.locationName}
          settings={calendarSettings}
          onClose={() => setOpenEditWarehouseModal(false)}
          onSaved={handleWarehouseSaved}
        />
      )}
    </div>
  );
};

export default ViewLocation;
